/**
 * Copyright (c) 2016 "Fronteer LTD". All rights reserved.
 */

import * as $ from 'jquery';
import * as jwtDecode from 'jwt-decode';

import * as AllyConfig from 'src/shared/rest/Config';

/**
 * The Instructure Canvas LMS
 *
 * @class
 * @implements {ExampleInterface}
 */
export function CanvasRestApi() {}

/**
 * Get the JWT token.
 *
 * This method will try to get the token from the local session storage first. If no token
 * can be retrieved, a new one will be fetched from the Canvas REST API, stored in the session
 * storage and returned to the caller.
 *
 * @param  {Function}   callback            Standard callback method
 * @param  {Object}     callback.err        Standard error object
 * @param  {String}     callback.token      The JWT token
 * @api private
 */
CanvasRestApi.prototype._getJwtToken = function(callback) {
    var self = this;

    // Try to get the JWT token from the session storage first
    var sessionToken = self._getJwtTokenFromSessionStorage();
    if (sessionToken) {
        return callback(null, sessionToken);
    }

    // If the token is not in the session storage, get it from
    // the REST API and cache it in the session storage.
    self._getJwtTokenFromApi(function(err, token) {
        if (err) {
            return callback(err);
        }

        self._setJwtTokenInSessionStorage(token);

        return callback(null, token);
    });
};

/**
 * Get the JWT token from the Canvas REST API.
 *
 * @param  {Function}   callback            Standard callback method
 * @param  {Object}     callback.err        Standard error object
 * @param  {String}     callback.token      The JWT token
 * @api private
 */
CanvasRestApi.prototype._getJwtTokenFromApi = function(callback) {
    var self = this;
    if (!AllyConfig.lti13Id()) {
        // Canvas stores the LTI launch url as the tool_launch_url for LTI 1.2 tools
        var launchUrl = encodeURIComponent(`${AllyConfig.baseUrl() }/api/v1/${ AllyConfig.clientId() }/lti/institution`);
        self._getExternalToolFromApi(launchUrl, callback);
    } else {
        // Canvas stores the final redirect url as the tool_launch_url for LTI 1.3 tools
        var baseUrlParts = AllyConfig.baseUrl().split('/');
        var scheme = baseUrlParts[0];
        var domain = `institution.${ baseUrlParts[2]}`;
        var redirectUrl = encodeURIComponent(`${scheme }//${ domain }/api/v2/auth/lti/1.3/target`);
        self._getExternalToolFromApi(redirectUrl, function(error, data) {
            if (error !== null && error !== undefined) {
                return callback(error);
            } else {
                // Unlike a LTI 1.2 launch, the JWT token doesn't contain user information.
                // Instead, it contains a login hint and lti_message_hint that can be used to get another JWT token
                // This other JWT token is termed an id token.
                var token = jwtDecode(data);

                // Call the authorize endpoint to obtain the user information.
                var nonce = Date.now();
                var url = `/api/lti/authorize?lti_message_hint=${
                    token.lti_message_hint }&client_id=${ AllyConfig.lti13Id()
                    }&redirect_uri=${ AllyConfig.baseUrl() }/api/v2/auth/lti/1.3/callback` +
                    `&login_hint=${ token.login_hint
                    }&nonce=${ nonce
                    }&scope=openid&response_type=id_token&response_mode=form_post&prompt=none`;
                $.ajax({
                    'url': url,
                    'dataType': 'html',
                    'success': function(data) {
                        // The Canvas authorization endpoint returns a html form with the id token embedded inside.
                        // eslint-disable-next-line max-len
                        // @see https://github.com/instructure/canvas-lms/blob/master/app/views/lti/ims/authentication/authorize.html.erb
                        var htmlNodes = $.parseHTML(data);
                        var form = htmlNodes.find(function (x) { return x.id === 'authorization_redirect_form';});
                        var idTokenHtml = form && $(form).find('#id_token');
                        var idToken = idTokenHtml && idTokenHtml.attr('value');
                        if (idToken !== null && idToken !== undefined) {
                            return callback(null, idToken);
                        } else {
                            return callback({ 'msg': 'Failed to authenticate user.' });
                        }
                    },
                    'error': function(xhr) {
                        return callback({ 'code': xhr.status, 'msg': xhr.responseText });
                    }
                });
            }
        });
    }
};

CanvasRestApi.prototype._getExternalToolFromApi = function(launchUrl, callback) {
    $.ajax({
        'url': `/api/lti/courses/${ AllyConfig.courseId() }/jwt_token?tool_launch_url=${ launchUrl}`,
        'dataType': 'json',
        'success': function(data) {
            return callback(null, data.jwt_token);
        },
        'error': function(xhr) {
            return callback({ 'code': xhr.status, 'msg': xhr.responseText });
        }
    });
};

/**
 * Get the JWT token from the session storage.
 *
 * Expired or invalid tokens will not be returned
 *
 * @return {[String]}       The stored token
 * @api private
 */
CanvasRestApi.prototype._getJwtTokenFromSessionStorage = function() {
    // Get the token data from the session storage
    var sessionKey = this._getSessionJwtKey();
    var rawSessionData = window.sessionStorage.getItem(sessionKey);
    if (!rawSessionData) {
        return null;
    }

    var sessionData = null;
    try {
        // Parse the session data. If this can't be parsed to JSON, it's either a token pre-dating ALLYPD-916 or
        // it's been corrupted somehow. Either way, we return null forcing a new token to be fetched and put in the
        // session storage.
        sessionData = JSON.parse(rawSessionData);

        // Parse the JWT token. If it can't be parsed it's most likely invalid, we return null so that a new token
        // can be retrieved.
        jwtDecode(sessionData.token);
    } catch (err) {
        return null;
    }

    // Compare the timestamp when the token was placed in the session storage to the current timestamp. This uses
    // the client's clock for both timestamps which prevents the issue where the client's clocks is wildly
    // out-of-date. To avoid generating false positives of expired tokens on the Ally backend server, we limit the
    // token's lifetime to 30 minutes on the client side.
    var cutOffPoint = Date.now() - (30 * 60 * 1000);
    if (sessionData.timeStamp < cutOffPoint) {
        return null;
    } else {
        return sessionData.token;
    }
};

/**
 * Set the JWT token in the session storage
 *
 * @param {String}      token               The token to store in the session storage
 * @api private
 */
CanvasRestApi.prototype._setJwtTokenInSessionStorage = function(token) {
    var sessionKey = this._getSessionJwtKey();
    try {
        window.sessionStorage.setItem(sessionKey, JSON.stringify({ 'token': token, 'timeStamp': Date.now() }));
    } catch (err) {
        // This can error on Safari in incognito mode. Rather than let the error bubble up, swallow it so that
        // functionality is not blocked. The downside of this is that now the JWT token will have to be retrieved
        // every time the user wants to interact with the Ally API.
    }
};

/**
 * Get the key that should be used to store a token against.
 *
 * This key will be unique to:
 *   - the Ally client id
 *   - the course id
 *   - the user's id (if any)
 *   - the user's role in the course
 *
 * This means that if a user looked at the course anonymously, signs in
 * as a course manager and returns to the course, they will get a new
 * session JWT key.
 *
 * @return {String}     The session key that should be used to store a token against
 * @api private
 */
CanvasRestApi.prototype._getSessionJwtKey = function() {
    var userId = window.ENV.current_user_id || '';
    var parts = [
        'ally',
        'jwt',
        AllyConfig.clientId(),
        AllyConfig.courseId(),
        userId,
        this._getRole()
    ];
    return parts.join('.');
};

/**
 * Determines the role of the current user in the course
 *
 * @return {String}         The role of the current user in the course
 * @api private
 */
CanvasRestApi.prototype._getRole = function() {
    // The roles the user has in the LMS. Note that these are *ALL* the
    // roles the user can have. e.g., if the user is an instructor in
    // course A and is currently on a page in course B, this array will
    // still contain `teacher` as a value.
    var roles = window.ENV.current_user_roles || [];

    // An administrator can safely be determined from the roles available
    // in the environment
    if (roles.indexOf('admin') !== -1) {
        return 'administrator';
    }

    // Only proceed if the user is looking at the Files or Pages tool
    var filesRights = window.ENV.FILES_CONTEXTS || [];
    var canManageFilesTool = (filesRights &&
                                filesRights[0] &&
                                filesRights[0].permissions &&
                                (filesRights[0].permissions.manage_files_add ||
                                    filesRights[0].permissions.manage_files_delete ||
                                    filesRights[0].permissions.manage_files_edit));
    var wikiRights = window.ENV.WIKI_RIGHTS || {};
    var canManageWikiPages = wikiRights.update;
    var isStudent = window.ENV.current_user_is_student;
    if (canManageWikiPages || canManageFilesTool || !isStudent) {
        return 'course-administrator';
    }

    // At this point we have to assume the user is a student/anonymous
    if (window.ENV.current_user_id) {
        return 'student';
    } else {
        return 'anonymous';
    }
};

/**
 * @see ExampleInterface.prototype.sign
 */
CanvasRestApi.prototype.sign = function(method, path, parameters, callback) {
    var self = this;
    self._getJwtToken(function(err, token) {
        if (err) {
            return callback(err);
        }

        var data = {
            'url': AllyConfig.baseUrl() + path,
            'headers': {
                'Authorization': `Bearer ${ token}`
            },
            'parameters': parameters
        };
        return callback(null, data);
    });
};

/**
 * @see ExampleInterface.prototype.http
 */
CanvasRestApi.prototype.http = function(method, path, parameters, opts, callback) {
    opts = opts || {};

    var self = this;
    self._getJwtToken(function(err, token) {
        if (err) {
            return callback(err);
        }

        var req = {
            'url': AllyConfig.baseUrl() + path,
            'type': method,
            'crossDomain': true,
            'xhrFields': {
                'withCredentials': false
            },
            'data': parameters,
            'success': function(data) {
                return callback(null, data);
            },
            'error': function(xhr) {
                return callback({ 'code': xhr.status, 'msg': xhr.responseText });
            }
        };
        req.headers = opts.headers || {};
        req.headers.Authorization = `Bearer ${ token}`;

        if (opts.contentType) {
            req.contentType = opts.contentType;
        }

        $.ajax(req);
    });
};

/**
 * @see ExampleInterface.prototype.getDownloadUrl
 */
CanvasRestApi.prototype.getDownloadUrl = function(fileId, callback) {
    var canvasBase = `${window.location.protocol }//${ window.location.host}`;
    var downloadUrl = `${canvasBase }/files/${ fileId }/download?download_frd=1`;
    return callback(null, downloadUrl);
};
